import { useState } from "react";
import { Platform, View } from "react-native";
import { Button } from "~/components/ui/Button";
import { RuleLine } from "~/components/ui/RuleLine";
import { BodyText, CaptionText } from "~/components/ui/Text";
import { useAuth } from "~/hooks/useAuth";
import { usePurchases } from "~/hooks/usePurchases";
import { pricingSummary, subscribeLabel } from "~/services/purchases";
import {
  DURANCE_MONTHLY_PRICE_TEXT,
  DURANCE_TRIAL_DAYS,
} from "~/services/revenuecat";
import { spacing, useTheme } from "~/styles/theme";

/**
 * The subscription webhook flips `profiles.subscription_status` a moment after
 * checkout completes, so after a purchase we re-read the profile until the
 * account shows as subscribed (or give up after ~30s and say so).
 */
const ACCESS_POLL_ATTEMPTS = 20;
const ACCESS_POLL_INTERVAL_MS = 1500;
const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * - `sync`: native Account screen — local use is free, the subscription
 *   turns on cross-device sync.
 * - `required`: the web paywall — there is no free tier on web.
 */
export type UpgradeVariant = "sync" | "required";

const INTRO: Record<UpgradeVariant, string> = {
  sync: `Sign-in is free. Syncing your counters across devices is ${DURANCE_MONTHLY_PRICE_TEXT}/month after a ${DURANCE_TRIAL_DAYS}-day free trial — start it below to turn sync on.`,
  required: `Durance on the web is a subscription. Start your ${DURANCE_TRIAL_DAYS}-day free trial now — after it ends you'll be charged ${DURANCE_MONTHLY_PRICE_TEXT}/month unless you cancel.`,
};

/**
 * Shown to a signed-in user whose account lacks an active subscription (never
 * subscribed, or a lapsed one). Purchasing here doesn't unlock anything
 * client-side by itself — it starts the RevenueCat subscription (a trial
 * counts), which the webhook then reflects into `profiles.subscription_status`;
 * `refresh()` re-reads that so the app picks up the change (see `~/hooks/useAuth`).
 */
export function UpgradePrompt({
  variant = "sync",
}: {
  variant?: UpgradeVariant;
}) {
  const { colors } = useTheme();
  const { user, refresh } = useAuth();
  const purchases = usePurchases(user?.id ?? null);
  const [activating, setActivating] = useState(false);
  const [notice, setNotice] = useState("");
  const useNativePaywall =
    purchases.supportsNativePaywall && Platform.OS !== "web";
  const busy = purchases.purchasing || activating;

  const afterEntitlementChange = async (entitled: boolean) => {
    if (!entitled) {
      await refresh();
      return;
    }
    setActivating(true);
    setNotice("");
    try {
      for (let attempt = 0; attempt < ACCESS_POLL_ATTEMPTS; attempt++) {
        const next = await refresh();
        if (next?.hasSyncAccess) return;
        await sleep(ACCESS_POLL_INTERVAL_MS);
      }
      setNotice(
        "Your subscription is confirmed, but it can take a minute to show up here. Reload in a moment if this doesn't clear.",
      );
    } finally {
      setActivating(false);
    }
  };

  const openPaywall = async () => {
    try {
      const result = await purchases.presentPaywall();
      await afterEntitlementChange(
        result === "purchased" || result === "restored",
      );
    } catch {
      // usePurchases already recorded the error in purchases.error.
    }
  };

  return (
    <View style={{ gap: spacing.md }}>
      <CaptionText color={colors.muted}>{INTRO[variant]}</CaptionText>

      {purchases.error ? (
        <BodyText color={colors.danger}>{purchases.error}</BodyText>
      ) : null}

      {notice ? <BodyText color={colors.muted}>{notice}</BodyText> : null}

      {activating ? (
        <BodyText color={colors.muted}>Activating your subscription…</BodyText>
      ) : null}

      {purchases.configured && purchases.packages.length === 0 ? (
        <BodyText color={colors.danger}>
          Plans aren’t available right now. Try again shortly.
        </BodyText>
      ) : null}

      <View style={{ gap: spacing.sm }}>
        {useNativePaywall ? (
          <Button
            label={purchases.purchasing ? "Opening…" : "Activate sync"}
            disabled={busy}
            fullWidth
            onPress={openPaywall}
          />
        ) : (
          purchases.packages.map((pkg) => (
            <View key={pkg.identifier} style={{ gap: spacing.xs }}>
              <Button
                label={subscribeLabel(pkg)}
                disabled={busy}
                fullWidth
                onPress={async () => {
                  try {
                    const info = await purchases.purchase(pkg);
                    await afterEntitlementChange(info.hasSync);
                  } catch {
                    // usePurchases already recorded the error, and a
                    // cancelled purchase is not one.
                  }
                }}
              />
              <CaptionText color={colors.muted}>
                {pricingSummary(pkg)}
              </CaptionText>
            </View>
          ))
        )}

        <RuleLine />

        <Button
          label={purchases.loading ? "Loading…" : "Restore purchases"}
          variant="secondary"
          fullWidth
          disabled={!purchases.configured || purchases.loading || busy}
          onPress={async () => {
            try {
              const info = await purchases.restore();
              await afterEntitlementChange(info.hasSync);
            } catch {
              // usePurchases already recorded the error.
            }
          }}
        />
      </View>
    </View>
  );
}
